import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LightbulbIcon } from "lucide-react";

import { Icon } from "@/components/atoms/Icon";

import { RichTextEditorReferencePopper } from "@/components/organisms/RichTextEditor/RichTextEditorReferencePopper";
import { PendingSessionProvider } from "@/components/organisms/PendingSession/PendingSession.store";
import { PendingChangesPanel } from "@/components/organisms/PendingSession/PendingSessionChangesPanel";
import {
  ThreadBox,
  ThreadBoxHandle,
} from "@/components/organisms/ThreadInput/ThreadInputBox";
import { ThreadComposer } from "@/components/organisms/ThreadInput/ThreadInputComposer";
import { ThreadTogglePopper } from "@/components/organisms/ThreadInput/ThreadInputTogglePopper";
import { viewSupportsNodePanel } from "@/app/app/_/layouts/nodePanelConfig";
import { idToTypename } from "@/objects/@utils";
import { State } from "@/state";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { Toggle } from "@/components/ui/toggle";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

import { ThreadMessageList } from "./ThreadMessageList";
import { ThreadScopeChip } from "./ThreadScopeChip";
import { ThreadShareChip } from "./ThreadShareChip";
import { useThreadPanel } from "./Thread.hooks";

export function Thread({
  showAllThreads = true,
  onEscape,
  onExpand,
  stickyBackdropColor,
}: {
  showAllThreads?: boolean;
  /** Escape from the composer / bridged thread panel. */
  onEscape?: () => void;
  /** Opens this thread on its own page; passed only by hosts that are not it. */
  onExpand?: () => void;
  /**
   * What this thread is laid over. A pinned prompt paints a band above itself
   * in it, so messages scroll behind rather than through the gap — and only
   * the host knows whether the thread sits on the panel or the content view.
   */
  stickyBackdropColor?: string;
}) {
  const activeThreadNodeId = State((s) => s.activeThreadNodeId);
  const activeMessageContext = State((s) => s.activeMessageContext);
  // What a page offers the chat about itself - its own record - stands apart
  // from what the user added to the message.
  const offered = useMemo(
    () => activeMessageContext.filter((reference) => reference.isPage),
    [activeMessageContext],
  );
  const added = useMemo(
    () => activeMessageContext.filter((reference) => !reference.isPage),
    [activeMessageContext],
  );
  const isPageContextEnabled = State((s) => s.isPageContextEnabled);
  // What the page offers, in a word: when it changes there is something new
  // to chat about.
  const offeredSignature = offered.map((reference) => reference.id).join(",");
  const togglePageContext = () =>
    State.getState().setIsPageContextEnabled(!isPageContextEnabled);
  // What the page offers about itself - the payment picked in triage, the
  // record open - as a toggle beside whose the chat is: on while it is sent
  // with every message, dashed while it is left out, as a filter to add is,
  // and a press does either. One toggle for both, not a chip that swaps its
  // parts: those ease everything as they change, and a swap showed an
  // outline fading out. Its bulb lights - a flash and a glow - as something
  // new is offered, and stays lit while it is sent.
  const pageContextButton =
    offered.length > 0 ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <Toggle
            variant="outline"
            size="sm"
            data-testid="thread-page-context"
            data-enabled={isPageContextEnabled || undefined}
            pressed={isPageContextEnabled}
            onPressedChange={togglePageContext}
            aria-label="Chat about this page"
            className={cn(
              // On the line beside whose the chat is, as wide as its words or
              // as the line has room for - its words cut short where it has
              // not.
              "flex-[1_1_0] min-w-0 max-w-max",
              "data-[state=off]:border-dashed data-[state=off]:text-muted-foreground",
            )}
          >
            <div
              key={offeredSignature}
              data-testid="thread-page-context-bulb"
              // Lit as something new is offered - a flash and a glow, its glow
              // going out over three seconds (``thread-page-context-aha``) - and
              // lit while it is sent: burnt orange on the light ground, where
              // amber is too pale to read as light; amber on the dark, where the
              // theme's greys would leave it no brighter lit than out.
              className={cn(
                "flex shrink-0 [--lit-bulb:#cc5500] dark:[--lit-bulb:#ffc078] animate-[thread-page-context-aha_3150ms_ease-out] motion-reduce:animate-none",
                isPageContextEnabled
                  ? "text-(--lit-bulb)"
                  : "text-muted-foreground/80",
              )}
            >
              <Icon ReactIcon={LightbulbIcon} size="sm" />
            </div>
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
              {offered
                .map((reference) => reference.name)
                .filter(Boolean)
                .join(", ")}
            </span>
          </Toggle>
        </TooltipTrigger>
        <TooltipContent side="top">
          {isPageContextEnabled
            ? "Sent with every message - click to leave it out"
            : "Left out - click to chat about it"}
        </TooltipContent>
      </Tooltip>
    ) : null;
  const threadBoxRef = useRef<ThreadBoxHandle>(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const threadPanelFocusRequestId = State((s) => s.threadPanelFocusRequestId);
  const isThreadPanelOpen = State((s) => s.isThreadPanelOpen);
  const activeAppView = State((s) => s.activeAppView);

  useEffect(() => {
    if (threadPanelFocusRequestId === 0) {
      return;
    }
    if (!isThreadPanelOpen) {
      return;
    }
    requestAnimationFrame(() => {
      if (!State.getState().isThreadPanelOpen) {
        return;
      }
      threadBoxRef.current?.focus();
    });
  }, [threadPanelFocusRequestId, isThreadPanelOpen]);

  const panel = useThreadPanel(activeThreadNodeId);
  // Files it was opened with - a photo taken or a file picked on a phone
  // (``openNewThreadWithFiles``) - attached to its draft as dropped ones are.
  // Taken off the store before they are attached, so a second panel does not
  // attach them again.
  const pendingFiles = State((state) => state.threadPanelPendingFiles);
  const { handleDropFiles } = panel;
  useEffect(() => {
    const files = State.getState().threadPanelPendingFiles;
    if (files.length === 0) return;
    State.setState({ threadPanelPendingFiles: [] });
    handleDropFiles(files);
  }, [pendingFiles, handleDropFiles]);
  const onConfirmAll = useCallback(
    () => panel.handleConfirmPending(panel.pendingSessionId),
    [panel],
  );
  const onRejectAll = useCallback(
    () => panel.handleRejectPending(panel.pendingSessionId),
    [panel],
  );

  // A thread that exists can be looked at for what it changed, wherever a
  // node panel can open beside it — the thread view always has one.
  const onShowChanges = useCallback(() => {
    if (!activeThreadNodeId) return;
    const state = State.getState();
    state.setActiveNodePanelGroupId("ThreadObject", "changes");
    state.openNodePanel(activeThreadNodeId, { autofocus: false });
  }, [activeThreadNodeId]);
  const canShowChanges =
    !!activeThreadNodeId &&
    (activeAppView === "thread" || viewSupportsNodePanel(activeAppView));

  // An open node panel showing a thread follows the active thread: switch
  // threads and it shows the one now in front of you. Only on a switch — a
  // thread the panel was opened on for its own sake (a rule's run) is left
  // alone until the user changes thread.
  const followedThreadNodeIdRef = useRef(activeThreadNodeId);
  useEffect(() => {
    if (followedThreadNodeIdRef.current === activeThreadNodeId) return;
    followedThreadNodeIdRef.current = activeThreadNodeId;
    const state = State.getState();
    if (
      !activeThreadNodeId ||
      !state.isNodePanelOpen ||
      !state.activePanelNodeId ||
      idToTypename(state.activePanelNodeId) !== "ThreadObject"
    ) {
      return;
    }
    state.setActivePanelNodeId(activeThreadNodeId, { autofocus: false });
  }, [activeThreadNodeId]);

  const pendingSessionContextValue = useMemo(
    () => ({
      pendingSessionId: panel.pendingSessionId,
      onConfirm: onConfirmAll,
      onReject: onRejectAll,
      isConfirming: panel.confirmingSession,
      isRejecting: panel.rejectingSession,
      onRefetch: panel.refetchThread,
    }),
    [panel, onConfirmAll, onRejectAll],
  );

  return (
    <PendingSessionProvider value={pendingSessionContextValue}>
      <div
        data-turn-phase={panel.turnPhase}
        data-testid="thread"
        // Files dropped anywhere on the thread are attached to the message
        // being written.
        onDragOver={(event) => {
          if (!event.dataTransfer.types.includes("Files")) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "copy";
          setIsDraggingFiles(true);
        }}
        onDragLeave={(event) => {
          if (event.currentTarget.contains(event.relatedTarget as Node | null))
            return;
          setIsDraggingFiles(false);
        }}
        onDrop={(event) => {
          if (!event.dataTransfer.types.includes("Files")) return;
          event.preventDefault();
          setIsDraggingFiles(false);
          panel.handleDropFiles([...event.dataTransfer.files]);
        }}
        className="pb-2 w-full flex-1 min-h-0 flex flex-col relative bg-transparent"
      >
        {isDraggingFiles && (
          <div
            data-testid="thread-file-drop-target"
            className="absolute inset-0 z-3 pointer-events-none flex items-center justify-center [border-width:2px] [border-style:dashed] border-ring rounded-lg bg-[color-mix(in_srgb,var(--background)_85%,transparent)]"
          >
            <Typography level="title-md" className="text-muted-foreground">
              Drop files to attach them to your message
            </Typography>
          </div>
        )}
        <div
          ref={panel.scrollHostRef}
          className="flex-1 w-full min-h-0 relative z-1"
        >
          <div
            ref={panel.rootRef}
            data-testid="thread-scroll"
            className="h-full w-full overflow-y-auto overflow-x-hidden"
          >
            {panel.isInitialThreadLoading ? (
              // A thread holds anything from a line to a dozen tool calls, so
              // no skeleton could stand for it: the large spinner turns in the
              // middle of where its messages will be.
              <div className="w-full h-full flex items-center justify-center">
                <Spinner className="size-8" />
              </div>
            ) : (
              <div className="flex flex-col min-h-full w-full max-w-180 ml-auto mr-auto pl-4 pr-4 pb-4">
                <div
                  className="flex-[1_1_auto] min-h-0 [overflow-anchor:none]"
                  aria-hidden
                />
                {panel.hasMoreMessages && (
                  <div
                    ref={panel.loadMoreRef}
                    className="shrink-0 h-full w-full [overflow-anchor:none]"
                    aria-hidden
                  />
                )}
                {panel.threadQueryError && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>
                      {panel.threadQueryError.message ||
                        "Could not load this thread. Please try again."}
                    </AlertDescription>
                  </Alert>
                )}
                <div className="shrink-0 w-full min-w-0 [overflow-anchor:none]">
                  <ThreadMessageList
                    messages={panel.messagesForList}
                    isWorkflowExecutionThread={panel.isWorkflowExecutionThread}
                    isStreaming={panel.awaitingAgentReply}
                    pendingPrompt={panel.pendingPrompt}
                    pendingContext={panel.pendingContext}
                    pendingFiles={panel.pendingFiles}
                    awaitingAgentReply={panel.awaitingAgentReply}
                    stopped={panel.stopRequested}
                    stickyBackdropColor={stickyBackdropColor}
                  />
                  {/* Reads as one more card in the conversation: the wording
                      carries the problem, so it needs no colour of its own. */}
                  {panel.turnNotice && (
                    <div
                      data-testid="thread-turn-notice"
                      className="mt-2 [overflow-anchor:none] flex items-center justify-between gap-3 pl-3 pr-3 pt-2 pb-2 [border-width:1px] [border-style:solid] border-border rounded-md bg-background"
                    >
                      <Typography
                        level="body-sm"
                        className="text-muted-foreground"
                      >
                        {panel.turnNotice.text}
                      </Typography>
                      <div className="flex gap-1 shrink-0">
                        {panel.turnNotice.action && (
                          <Button
                            variant="outline"
                            size="xs"
                            onClick={panel.turnNotice.action.onClick}
                          >
                            {panel.turnNotice.action.label}
                          </Button>
                        )}
                        {panel.turnNotice.onDismiss && (
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={panel.turnNotice.onDismiss}
                          >
                            Dismiss
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div
                  className="shrink-0 h-px w-full [overflow-anchor:auto]"
                  aria-hidden
                />
              </div>
            )}
          </div>
        </div>

        <div className="w-full max-w-180 ml-auto mr-auto pl-4 pr-4 shrink-0 mb-2 z-2 relative">
          {/* What the page offers about itself - the payment picked in
              triage, the record open - along the composer's foot beside its
              send button: sent with every message while its switch is on,
              and not at all once it is off. */}
          <div className="relative z-1">
            <ThreadBox
              ref={threadBoxRef}
              // Whose the chat is - a business's, or the person's own - and,
              // before its first message is on its way, where it is made.
              scope={
                <>
                  <ThreadScopeChip
                    entity={panel.threadFragment?.entity ?? null}
                    pickedIdentifier={panel.newThreadEntityIdentifier}
                    onPick={
                      panel.activeThreadNodeId ||
                      panel.threadFragment ||
                      panel.creatingThreadLoading ||
                      panel.pendingPrompt
                        ? null
                        : panel.setNewThreadEntityIdentifier
                    }
                  />
                  <ThreadShareChip thread={panel.threadFragment ?? null} />
                  {pageContextButton}
                </>
              }
              header={
                <PendingChangesPanel
                  pendingNodes={panel.pendingNodes}
                  expanded={panel.pendingChangesExpanded}
                  onToggleExpanded={() =>
                    panel.setPendingChangesExpanded((v) => !v)
                  }
                  onConfirmAll={onConfirmAll}
                  onRejectAll={onRejectAll}
                  confirmingSession={panel.confirmingSession}
                  rejectingSession={panel.rejectingSession}
                  entityIdentifier={
                    panel.threadFragment?.entity?.identifier ?? null
                  }
                  isReviewing={panel.isReviewingChanges}
                />
              }
              // Its foot its buttons alone: what the page offers is a chip
              // beside whose the chat is.
              footer={null}
              key={activeThreadNodeId || "new-thread"}
              isMainThread
              showThreadToggle={showAllThreads}
              mode={
                State.getState().activeMessageIndex >= 0 ? "edit" : "readonly"
              }
              activeMessageContext={added}
              files={panel.draftFiles.map(({ key, name, messageFileId }) => ({
                key,
                name,
                isUploading: !messageFileId,
              }))}
              onRemoveFile={panel.handleRemoveDraftFile}
              ThreadComposerComponent={ThreadComposer}
              ThreadTogglePopperComponent={ThreadTogglePopper}
              ThreadReferencePopperComponent={RichTextEditorReferencePopper}
              onSubmit={panel.handleSendMessage}
              onStop={
                panel.canStop && !panel.isWorkflowExecutionThread
                  ? panel.handleStopThread
                  : undefined
              }
              stopping={panel.stoppingThread}
              onExpand={onExpand}
              onShowChanges={canShowChanges ? onShowChanges : undefined}
              onEscape={onEscape}
              onRemoveReference={(id) => {
                const {
                  setActiveMessageContext: setCtx,
                  activeMessageContext: ctx,
                } = State.getState();
                setCtx(ctx.filter((c) => c.id !== id));
              }}
              onModeChange={() => {}}
              onSelectThread={(id) =>
                panel.openThreadTab(id, { activate: true })
              }
              onAddReference={(ref) => {
                const {
                  setActiveMessageContext: setCtx,
                  activeMessageContext: ctx,
                } = State.getState();
                setCtx([
                  ...ctx.filter((c) => c.id !== ref.id),
                  {
                    typename: ref.__typename,
                    id: ref.id,
                    identifier: ref.identifier,
                    name: ref.name,
                  },
                ]);
              }}
              onNewThread={panel.resetThread}
            />
          </div>
        </div>
      </div>
    </PendingSessionProvider>
  );
}
